import React, { useState, useEffect } from 'react';

import { Checkbox } from 'antd';
import Axios from 'axios';

import { saxonize } from '@libs/NwUtils';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { useProject } from '@hooks/API/Project/UseProject';
import { useProjectAccountingDetails } from '@hooks/API/Project/UseProjectAccountingDetails';

import ContactDropdown from '@components/ContactDropdown/ContactDropdown';
import { Loading } from '@components/Gui/Loading';
import NwDivider from '@components/Gui/NwDivider';
import NwDrawer from '@components/Gui/NwDrawer';
import PdfPrintDrawer from '@components/PdfPrintDrawer/PdfPrintDrawer';
import ButtonTemplateSelector from '@components/TemplateSelector/ButtonTemplateSelector';

import { getModelConfirmationProperties, getTransactionConfirmationProperties } from '../ProjectUtils';
import ProjectPrintEventItem from './ProjectPrintEventItem';
import ReportCreationDrawerTransactionItem from './ReportCreationDrawerTransactionItem';
import { ReportCreationDrawerWrapper } from './ReportCreationDrawerUI';

const ReportCreationDrawer = ({ projectId, onClose, modelId }) => {
    const { data: currentProjectAccountingDetails, isFetching: isFetchingProjectAccountingDetails } =
        useProjectAccountingDetails(projectId);
    const { data: currentProject, isFetching: isFetchingProject } = useProject(projectId);
    //const [template, setTemplate] = useState(null);
    const [callsheet, setCallsheet] = useState([]);

    const getProjectCallSheets = async () => {
        try {
            const callsheets = await Axios.get(`/projects/callsheets/${projectId}`);
            callsheets.sort((a, b) => a.CallSheetType.ID - b.CallSheetType.ID);
            const callsheetsWithActiveProperty = callsheets.map((callsheet) => ({
                ...callsheet,
                active: true,
            }));
            setCallsheet(callsheetsWithActiveProperty);
        } catch (error) {
            console.error('Get Project Callsheets Error => ', error);
        }
    };

    const [usages, setUsages] = useState(
        currentProject && currentProject.Usages
            ? currentProject.Usages.map((usage) => ({
                  ...usage,
                  active: true,
              }))
            : []
    );

    const [otherFees, setOtherFees] = useState([]);
    const [modelsFees, setModelsFees] = useState([]);
    const [docVersion, setDocVersion] = useState(null);
    const [selectedModelId, setSelectedModelId] = useState(modelId ? modelId : null);
    const [projectEvents, setProjectEvents] = useState(null);
    const [isFillingTemplate, setIsFillingTemplate] = useState(false);

    const handleSelectModel = (modelId) => {
        setSelectedModelId(modelId);
    };

    useEffect(() => {
        let otherFeesList = [];
        let modelsFeesList = [];

        if (currentProject && currentProjectAccountingDetails && currentProjectAccountingDetails.Transactions) {
            // Check if there are transactions and set the flag
            for (const transaction of currentProjectAccountingDetails.Transactions) {
                if (!transaction.ModelID) {
                    const hydratedTransaction = {
                        ...transaction,
                        key: transaction.ID,
                        active: true,
                    };
                    otherFeesList.push(hydratedTransaction);
                } else {
                    if (!selectedModelId || transaction.ModelID === selectedModelId) {
                        const modelFeesList = modelsFeesList.find((mf) => mf.ModelID === transaction.ModelID);
                        const confProp = getTransactionConfirmationProperties(currentProject, transaction);
                        const hasConfProp = confProp.Confirmed !== null;

                        const hydratedTransaction = {
                            ...transaction,
                            key: transaction.ID,
                            ConfirmationProperties: hasConfProp ? { ...confProp } : null,
                        };
                        if (modelFeesList) {
                            modelFeesList.transactions.push({
                                ...hydratedTransaction,
                                active: hasConfProp ? confProp.Confirmed && !confProp.Cancel : true,
                            });
                        } else {
                            modelsFeesList.push({
                                ModelID: transaction.ModelID,
                                ModelConfirmationProperties: getModelConfirmationProperties(
                                    currentProject,
                                    transaction.ModelID
                                ),
                                transactions: [
                                    {
                                        ...hydratedTransaction,
                                        active: hasConfProp ? confProp.Confirmed && !confProp.Cancel : true,
                                    },
                                ],
                                active: true,
                            });
                        }
                    }
                }
            }
        }
        setModelsFees([...modelsFeesList]);
        setOtherFees([...otherFeesList]);
    }, [currentProjectAccountingDetails, currentProject, selectedModelId]); //selectedBillingInfoId

    useEffect(() => {
        const tempProjectEvents = {};
        if (currentProject) {
            if (selectedModelId) {
                tempProjectEvents.EventsList = currentProject.Events.filter((event) =>
                    event.EventDates.some((ed) => ed.EventDatesModels.some((em) => em.ModelID === selectedModelId))
                ).map((event) => ({
                    ...event,
                    active: true,
                    CheckIDs: [event.ID],
                }));
            } else {
                tempProjectEvents.EventsList = currentProject.Events.map((event) => ({
                    ...event,
                    active: true,
                    CheckIDs: [event.ID],
                }));
            }
            if (selectedModelId) {
                tempProjectEvents.TravelsList = currentProject.Travels.filter((event) =>
                    event.Models.find((em) => em.ModelID === selectedModelId)
                ).map((event) => ({
                    ...event,
                    active: true,
                    CheckIDs: [event.Models.find((em) => em.ModelID === selectedModelId).TravelID],
                }));
            } else {
                tempProjectEvents.TravelsList = currentProject.Travels.map((event) => ({
                    ...event,
                    active: true,
                    CheckIDs: [...event.GroupedEventIDs],
                }));
            }

            if (selectedModelId) {
                tempProjectEvents.AccommodationsList = currentProject.Accommodations.filter((event) =>
                    event.Models.find((em) => em.ModelID === selectedModelId)
                ).map((event) => ({
                    ...event,
                    active: true,
                    CheckIDs: [event.Models.find((em) => em.ModelID === selectedModelId).AccommodationID],
                }));
            } else {
                tempProjectEvents.AccommodationsList = currentProject.Accommodations.map((event) => ({
                    ...event,
                    active: true,
                    CheckIDs: [...event.GroupedEventIDs],
                }));
            }
            if (selectedModelId) {
                tempProjectEvents.ActivitiesList = currentProject.FollowUps.filter((event) =>
                    event.Models.find((em) => em.ModelID === selectedModelId)
                ).map((event) => ({
                    ...event,
                    active: true,
                    CheckIDs: [event.Models.find((em) => em.ModelID === selectedModelId).FollowUpID],
                }));
            } else {
                tempProjectEvents.ActivitiesList = currentProject.FollowUps.map((event) => ({
                    ...event,
                    active: true,
                    CheckIDs: [...event.GroupedEventIDs],
                }));
            }
            setProjectEvents(tempProjectEvents);
            getProjectCallSheets();
        }
    }, [currentProject, selectedModelId]);

    const handleUpdateUsage = (usageIndex) => (active) => {
        const newUsages = [...usages];
        newUsages[usageIndex].active = active.target.checked;
        setUsages(newUsages);
    };

    const handleUpdateCallsheet = (callsheetIndex) => (active) => {
        const newCallsheet = [...callsheet];
        newCallsheet[callsheetIndex].active = active.target.checked;
        setCallsheet(newCallsheet);
    };

    const handleUpdateModelFees = (selectedFees, modelID) => {
        let newModelsFees = [...modelsFees];
        const modelIndex = newModelsFees.findIndex((modelFees) => modelFees.ModelID === modelID);
        for (const t of newModelsFees[modelIndex].transactions) {
            if (selectedFees.includes(t.ID)) {
                t.active = true;
            } else {
                t.active = false;
            }
        }
        setModelsFees(newModelsFees);
    };

    const handleUpdateOtherFees = (selectedFees) => {
        let newOtherFees = [...otherFees];
        for (const t of newOtherFees) {
            if (selectedFees.includes(t.ID)) {
                t.active = true;
            } else {
                t.active = false;
            }
        }
        setOtherFees(newOtherFees);
    };

    // const handleChangeModel = modelId => active => {
    //     let newModelsFees = [...modelsFees];
    //     const modelIndex = newModelsFees.findIndex(modelFees => modelFees.ModelID === modelId);
    //     newModelsFees[modelIndex].active = active.target.checked;
    //     setModelsFees(newModelsFees);
    // };

    const handleChangeEvents = (eventIndex) => (active) => {
        const newEventsList = [...projectEvents.EventsList];
        newEventsList[eventIndex].active = active.target.checked;
        setProjectEvents({ ...projectEvents, EventsList: newEventsList });
    };

    const handleChangeTravels = (eventIndex) => (active) => {
        const newEventsList = [...projectEvents.TravelsList];
        newEventsList[eventIndex].active = active.target.checked;
        setProjectEvents({ ...projectEvents, TravelsList: newEventsList });
    };

    const handleChangeAccommodations = (eventIndex) => (active) => {
        const newEventsList = [...projectEvents.AccommodationsList];
        newEventsList[eventIndex].active = active.target.checked;
        setProjectEvents({ ...projectEvents, AccommodationsList: newEventsList });
    };

    // const handleChangeActivities = eventIndex => active => {
    //     const newEventsList = [...projectEvents.ActivitiesList];
    //     newEventsList[eventIndex].active = active.target.checked;
    //     setProjectEvents({ ...projectEvents, ActivitiesList: newEventsList });
    // };

    const handleGenerateContract = async (templateid) => {
        setIsFillingTemplate(true);
        const TransactionIDs = otherFees.filter((fee) => fee.active).map((fee) => fee.ID);
        for (const modelFees of modelsFees) {
            TransactionIDs.push(...modelFees.transactions.filter((fee) => fee.active).map((fee) => fee.ID));
        }
        const EventIDs = projectEvents.EventsList.filter((event) => event.active)
            .map((event) => event.CheckIDs)
            .flat();
        const TravelIDs = projectEvents.TravelsList.filter((event) => event.active)
            .map((event) => event.CheckIDs)
            .flat();
        const AccommodationIDs = projectEvents.AccommodationsList.filter((event) => event.active)
            .map((event) => event.CheckIDs)
            .flat();
        const FollowUpIDs = projectEvents.ActivitiesList.filter((event) => event.active)
            .map((event) => event.CheckIDs)
            .flat();
        const CallSheetIDs = callsheet.filter((cs) => cs.active).map((cs) => cs.ID);
        const UsagesIDs = usages.filter((usage) => usage.active).map((usage) => usage.ID);

        //create data object to send with all IDs array
        const data = {
            Template: templateid,
            Area: 'ProjectReport',
            OutputFileName: 'Report',
            //BillingClientInfoID: selectedBillingInfoId,
            ProjectID: projectId,
            ModelID: selectedModelId,
            TransactionIDs,
            EventIDs,
            TravelIDs,
            AccommodationIDs,
            FollowUpIDs,
            CallSheetIDs,
            UsagesIDs,
        };

        const contractResponse = await Axios.post('/projects/printreport', data);
        setDocVersion(contractResponse);
        setIsFillingTemplate(false);
    };

    const getUsageTypes = (usage) => {
        if (usage.UsageTypes && usage.UsageTypes.length > 0) {
            return usage.UsageTypes.join(', ');
        }
        return 'General';
    };

    const getUsageAreas = (usage) => {
        if (usage.UsageTypes && usage.Areas.length > 0) {
            return usage.Areas.join(', ');
        }
        return 'All Areas';
    };

    return (
        <>
            <NwDrawer width="70vw" title="Print Project Report" onClose={onClose}>
                {isFetchingProject || isFetchingProjectAccountingDetails ? (
                    <Loading textBlack />
                ) : (
                    <ReportCreationDrawerWrapper>
                        <div className="export-options">
                            <p>Print Report for:</p>
                            <ContactDropdown
                                activeTypes={['mod']}
                                activableTypes={[]}
                                customPlaceholder={'All models'}
                                projectId={projectId}
                                value={selectedModelId}
                                onChange={handleSelectModel}
                            />
                        </div>
                        <div className="export-content">
                            {projectEvents && (
                                <div>
                                    {projectEvents.EventsList.length > 0 && (
                                        <>
                                            <NwDivider>Events</NwDivider>
                                            <div className="events-container">
                                                {projectEvents.EventsList.map((event, eventIndex) => (
                                                    <div className="event-container" key={`event-${eventIndex}`}>
                                                        <ProjectPrintEventItem
                                                            checked={event.active}
                                                            event={event}
                                                            onChange={handleChangeEvents(eventIndex)}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                    {projectEvents.TravelsList.length > 0 && (
                                        <>
                                            <NwDivider>Travels</NwDivider>
                                            <div className="events-container">
                                                {projectEvents.TravelsList.map((event, eventIndex) => (
                                                    <div className="event-container" key={`event-${eventIndex}`}>
                                                        <ProjectPrintEventItem
                                                            checked={event.active}
                                                            event={event}
                                                            onChange={handleChangeTravels(eventIndex)}
                                                            type="Travel"
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                    {projectEvents.AccommodationsList.length > 0 && (
                                        <>
                                            <NwDivider>Accommodations</NwDivider>
                                            <div className="events-container">
                                                {projectEvents.AccommodationsList.map((event, eventIndex) => (
                                                    <div className="event-container" key={`event-${eventIndex}`}>
                                                        <ProjectPrintEventItem
                                                            checked={event.active}
                                                            event={event}
                                                            onChange={handleChangeAccommodations(eventIndex)}
                                                            type="Accommodation"
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                    {/* {(projectEvents.ActivitiesList.length > 0) &&
                                        <>
                                            <NwDivider>Activities</NwDivider>
                                            <div className="events-container">
                                                {projectEvents.ActivitiesList.map((event, eventIndex) => (
                                                    <div className="event-container" key={`event-${eventIndex}`}>
                                                        <Checkbox
                                                            checked={event.active}
                                                            onChange={handleChangeActivities(eventIndex)}>
                                                            <FlexContainer>
                                                                <div className='event-tag'>
                                                                    <EventTypeTag event={event} type="FollowUp" />
                                                                </div>
                                                                <div className='event-title'>{event.Title}</div>
                                                            </FlexContainer>
                                                        </Checkbox>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    } */}
                                </div>
                            )}
                            {modelsFees.length > 0 && (
                                <div className="models-transactions-container">
                                    <NwDivider>Fees & Expenses</NwDivider>
                                    {modelsFees.map((modelFees, index) => {
                                        return (
                                            <div
                                                className="model-transactions-container"
                                                key={`model-fees-${modelFees.ModelID}-${index}`}
                                            >
                                                <h5>
                                                    {saxonize(getContactName(modelFees.ModelID))} fees [
                                                    {modelFees.ModelConfirmationProperties.ConfirmedWorkDays} confirmed
                                                    work dates]
                                                </h5>
                                                <div className="transactions-container">
                                                    <ReportCreationDrawerTransactionItem
                                                        modelID={modelFees.ModelID}
                                                        transactions={modelFees.transactions}
                                                        project={currentProject}
                                                        onSelect={handleUpdateModelFees}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {otherFees.length > 0 && (
                                <div className="other-transactions-container">
                                    <h5>other project fees</h5>
                                    <div className="transactions-container">
                                        <ReportCreationDrawerTransactionItem
                                            transactions={otherFees}
                                            project={currentProject}
                                            onSelect={handleUpdateOtherFees}
                                        />
                                    </div>
                                </div>
                            )}
                            {usages.length > 0 && (
                                <div className="usages-container">
                                    <NwDivider>Usages</NwDivider>
                                    {usages.map((usage, index) => {
                                        return (
                                            <div key={`usage-${usage.ID}`} className="usage-container">
                                                <Checkbox checked={usage.active} onChange={handleUpdateUsage(index)}>
                                                    <h2>{`${getUsageTypes(usage)} - ${getUsageAreas(usage)}`}</h2>
                                                    <p>{usage.Duration}</p>
                                                    <p>{usage.Description}</p>
                                                </Checkbox>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {callsheet.length > 0 && (
                                <div className="callsheet-container">
                                    <NwDivider>Callsheet</NwDivider>
                                    {callsheet.map((cs, index) => {
                                        return (
                                            <div key={`usage-${cs.ID}`} className="callsheet-container">
                                                <Checkbox checked={cs.active} onChange={handleUpdateCallsheet(index)}>
                                                    <h2>{cs.CallSheetType.Name}</h2>
                                                    <p>{cs.Name}</p>
                                                </Checkbox>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div style={{ textAlign: 'center', margin: '1rem' }}>
                            <ButtonTemplateSelector
                                loading={isFillingTemplate}
                                templateArea="ProjectReport"
                                onClick={handleGenerateContract}
                            />
                        </div>
                    </ReportCreationDrawerWrapper>
                )}
            </NwDrawer>
            {docVersion && (
                <NwDrawer
                    onClose={() => setDocVersion(null)}
                    padding="0px"
                    title="Print Report"
                    visible={true}
                    whiteBg={false}
                    width="80vw"
                    maxWidth={'1024px'}
                >
                    <PdfPrintDrawer docVersion={docVersion} />
                </NwDrawer>
            )}
        </>
    );
};

export default ReportCreationDrawer;
